import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import OTPInput from "react-otp-input";
import { motion } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../services/interceptor";

const capitalizeName = (rawName = "") => {
  return rawName
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const isValidEmail = (email) => {
  // Simple, reasonably safe email regex
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const isValidMobile = (mobile) => {
  // Strip non-digits then validate Indian 10-digit numbers starting with 6-9
  const digits = (mobile || "").replace(/\D/g, "");
  return /^[6-9]\d{9}$/.test(digits);
};

const Signup = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { loginUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (loginUser) {
      navigate("/dashboard");
    }
  }, [loginUser, navigate]);

  const handleSignupSubmit = async (e) => {
    e.preventDefault();

    const finalName = capitalizeName(name);
    setName(finalName);

    if (!finalName) {
      toast.error("Please enter your name.", { position: "top-center" });
      return;
    }

    if (!isValidMobile(mobile)) {
      toast.error("Please enter a valid 10-digit mobile number (India).", {
        position: "top-center",
      });
      return;
    }

    if (!isValidEmail(email)) {
      toast.error("Please enter a valid email address.", { position: "top-center" });
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match", { position: "top-center" });
      return;
    }

    if (password.length < 6) {
      toast.error("Password should be at least 6 characters.", { position: "top-center" });
      return;
    }

    setLoading(true);
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/api/register`, {
        name: finalName,
        mobile: mobile.replace(/\D/g, ""),
        email,
        password,
      });

      setOtpSent(true);
      toast.success("OTP sent successfully! Please check your email.", {
        position: "top-center",
      });
    } catch (error) {
      console.error("Signup failed", error);
      toast.error(error.response?.data?.message || "Registration failed", {
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/api/verify-otp`, {
        email,
        otp,
      });
      setIsVerified(true);
      toast.success("Account verified successfully! Please sign in.", {
        position: "top-center",
      });
      setTimeout(() => navigate("/login"), 2000);
    } catch (error) {
      console.error("OTP verification failed", error);
      toast.error("Invalid OTP. Please try again.", { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (otpValue) => {
    setOtp(otpValue);
  };

  // Ensure mobile input only contains digits while typing
  const handleMobileChange = (val) => {
    const digits = val.replace(/\D/g, "");
    setMobile(digits);
  };

  // OTP Verification Screen
  if (otpSent && !isVerified) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{
          duration: 0.6,
          ease: "easeInOut",
        }}
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-cyan-50 py-12 px-4 sm:px-6 lg:px-8"
      >
        <div className="bg-white shadow-xl rounded-2xl p-8 max-w-md w-full border border-gray-100">
          {/* Logo and Clinic Info */}
          <div className="text-center mb-8">
            <div className="mb-4">
              <img
                src="/logo.png"
                alt="Dental Clinic Logo"
                className="mx-auto h-16 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div className="hidden mx-auto h-16 w-16 bg-blue-100 rounded-full items-center justify-center">
                <svg className="h-8 w-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Account</h1>
            <p className="text-sm text-gray-600 mb-6">
              An OTP has been sent to <span className="font-medium text-blue-600">{email}</span>
            </p>
          </div>

          <form onSubmit={handleOtpSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                Enter 6-digit verification code
              </label>
              <OTPInput
                value={otp}
                onChange={handleOTPChange}
                numInputs={6}
                renderInput={(props) => (
                  <input
                    {...props}
                    placeholder="0"
                    className="w-12 h-12 border-2 border-gray-300 bg-white rounded-lg text-center text-lg font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-all duration-200"
                    style={{
                      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                    }}
                  />
                )}
                containerStyle={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "4px",
                }}
              />
            </div>

            <button
              type="submit"
              className="w-full px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={loading || otp.length !== 6}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Verifying...
                </div>
              ) : (
                "Verify Account"
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setOtpSent(false)}
                className="text-blue-600 font-medium hover:text-blue-700 hover:underline transition-colors duration-200 text-sm"
              >
                ← Back to registration
              </button>
            </div>
          </form>
        </div>
        <ToastContainer position="top-center" theme="colored" className="mt-16" />
      </motion.div>
    );
  }

  // Registration Form Screen
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{
        duration: 0.6,
        ease: "easeInOut",
      }}
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-cyan-50 py-12 px-4 sm:px-6 lg:px-8"
    >
      <div className="bg-white shadow-xl rounded-2xl p-8 max-w-md w-full border border-gray-100">
        {/* Logo and Clinic Info */}
        <div className="text-center mb-8">
          <div className="mb-4">
            <img
              src="/logo.png"
              alt="Dental Clinic Logo"
              className="mx-auto h-16 w-auto object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden mx-auto h-16 w-16 bg-blue-100 rounded-full items-center justify-center">
              <svg className="h-8 w-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Request Access</h1>
          <p className="text-sm text-gray-600 mb-6">Mahalaxmi Dental Clinic - Create your account</p>
        </div>

        <form onSubmit={handleSignupSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              id="name"
              type="text"
              placeholder="Enter your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setName(capitalizeName(name))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
              required
            />
          </div>

          <div>
            <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
            <input
              id="mobile"
              type="tel"
              inputMode="tel"
              placeholder="Enter your mobile number"
              value={mobile}
              onChange={(e) => handleMobileChange(e.target.value)}
              maxLength={10}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
              required
            />
            <p className="text-xs text-gray-400 mt-1">Enter 10-digit mobile number (e.g. 9123456789)</p>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
              required
            />
          </div>

          <div className="relative">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
              required
            />
          </div>

          <div className="relative">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 focus:outline-none text-sm"
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>

          <button
            type="submit"
            className="w-full px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Creating Account...
              </div>
            ) : (
              "Request Access"
            )}
          </button>

          <div className="text-center mt-6 pt-4 border-t border-gray-200">
            <span className="text-gray-600 text-sm">Already have an account? </span>
            <Link to="/login" className="text-blue-600 font-medium hover:text-blue-700 hover:underline transition-colors duration-200">Sign In</Link>
          </div>
        </form>

        {/* Additional dental clinic info */}
        <div className="mt-8 pt-6 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-500">For technical support, contact IT at <a href="mailto:support.mdc@ricecity.in" className="text-blue-600 hover:underline">support.mdc@ricecity.in</a></p>
        </div>
      </div>

      <ToastContainer position="top-center" theme="colored" className="mt-16" />
    </motion.div>
  );
};

export default Signup;
